'use client';

/**
 * ZineHeader — the sticky B1 zine masthead shared by every top-level
 * page (/active, /yarn, /inventory, /profile, /settings, …).
 *
 * Before this primitive the same ~50-line block was copy-pasted into
 * six files: a `position: sticky` header with the cream + halftone
 * radial-gradient texture, `paddingTop: env(safe-area-inset-top)`, a
 * mono-caps `←` back button, the `<PageTitle>`, and a 1px ink hairline
 * rule underneath. Extracted in the 1394–1411 review sweep.
 *
 * The masthead chrome is fixed; the parts that vary are props:
 *   - `onBack` / `backLabel` — the `←` button. Omitted → no back button.
 *   - `head` / `tail` / `titleScale` / `titleStyle` — forwarded to
 *     <PageTitle>. Or pass `title` to render a fully custom node
 *     instead (e.g. a chapter-detail headline).
 *   - `right` — a trailing slot, typically <MastheadAddButton>.
 *   - `children` — rendered BELOW the hairline rule, still inside the
 *     sticky header (e.g. the /active tagline strip).
 */

import type { CSSProperties, MouseEventHandler, ReactNode } from 'react';
import { PageTitle, type PageTitleScale } from './PageTitle';

const HEADER_STYLE: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 40,
  // Same cream-paper halftone as the body so the grain runs
  // continuously top to bottom through the masthead.
  backgroundColor: 'var(--paper-1)',
  backgroundImage:
    'radial-gradient(circle, rgba(15,14,12,0.10) 1px, transparent 1.4px)',
  backgroundSize: '4px 4px',
  paddingTop: 'env(safe-area-inset-top)',
};

const HAIRLINE_STYLE: CSSProperties = {
  height: 1,
  background: 'var(--ink)',
  marginLeft: 14,
  marginRight: 14,
  marginBottom: 8,
};

const BACK_BUTTON_STYLE: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  fontFamily: 'var(--font-mono)',
  fontSize: 9,
  letterSpacing: '0.18em',
  color: 'var(--ink)',
  cursor: 'pointer',
  textTransform: 'uppercase',
  lineHeight: 1,
  marginBottom: 4,
  display: 'block',
};

export type ZineHeaderProps = {
  /** Display-caps head word(s). Ignored when `title` is set. */
  head?: string;
  /** Italic-serif tail. Ignored when `title` is set. */
  tail?: string;
  /** <PageTitle> size preset. Default 'lg'. */
  titleScale?: PageTitleScale;
  /** Style overrides forwarded to <PageTitle>. */
  titleStyle?: CSSProperties;
  /** Fully custom title node — overrides head/tail/titleScale. */
  title?: ReactNode;
  /** Back-button handler. Omit to hide the `←` button. */
  onBack?: MouseEventHandler<HTMLButtonElement>;
  /** Accessible label for the back button. Default "Back". */
  backLabel?: string;
  /** Trailing slot — usually <MastheadAddButton>. */
  right?: ReactNode;
  /** Content rendered below the hairline rule, inside the header. */
  children?: ReactNode;
};

export function ZineHeader({
  head,
  tail,
  titleScale = 'lg',
  titleStyle,
  title,
  onBack,
  backLabel = 'Back',
  right,
  children,
}: ZineHeaderProps) {
  return (
    <header style={HEADER_STYLE}>
      <div
        style={{
          padding: '10px 14px 8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {onBack && (
            <button onClick={onBack} aria-label={backLabel} style={BACK_BUTTON_STYLE}>
              ←
            </button>
          )}
          {title ?? (
            <PageTitle head={head ?? ''} tail={tail} scale={titleScale} style={titleStyle} />
          )}
        </div>
        {right}
      </div>
      <div aria-hidden style={HAIRLINE_STYLE} />
      {children}
    </header>
  );
}
